import { Controller } from "@hotwired/stimulus"
import ClipBuilderApi from "../services/clip_builder_api"
import {
  serializeOverlay,
  toFormValues,
  buildOverlayEnvelope,
  parseOverlayEnvelope,
  OVERLAY_KIND_TO_FIELD,
} from "../services/overlay_serializer"
import { readOverlayForm, writeOverlayForm } from "../utils/overlay_form_io"
import { libraryEmptyMarkup, libraryErrorMarkup } from "../utils/clip_builder_markup"
import { populateLibraryGrid } from "../utils/logo_library"

/**
 * Clip Overlays Controller
 *
 * Owns the four inline overlay editors (speech bubble, title, logo, subscribe
 * button) plus cross-clip copy/paste. Extracted from the clip_builder
 * monolith: the form markup is server-rendered (OverlayFormsComponent
 * <template>s cloned via the *Template targets), the seconds/frames + defaults
 * math lives in the overlay_serializer service, and requests go through
 * ClipBuilderApi. This controller only clones, pre-fills, wires, and submits.
 */
export default class extends Controller {
  static values = { updateUrl: String, uploadUrl: String, segmentIndex: Number }
  static targets = ["bubbleTemplate", "titleTemplate", "logoTemplate", "subscribeTemplate"]

  get api() {
    return new ClipBuilderApi({
      updateUrl: this.updateUrlValue,
      uploadUrl: this.uploadUrlValue,
      segmentIndex: this.segmentIndexValue,
    })
  }

  // --- Add / edit entry points (bound from the clip card) ---

  addBubble(event) { this._openForm("bubble", event, null) }
  editBubble(event) { this._openForm("bubble", event, this._json(event, "bubbleJson")) }
  addTitle(event) { this._openForm("title", event, null) }
  editTitle(event) { this._openForm("title", event, this._json(event, "titleJson")) }
  addLogo(event) { this._openForm("logo", event, null) }
  editLogo(event) { this._openForm("logo", event, this._json(event, "logoJson")) }
  addSubscribe(event) { this._openForm("subscribe", event, null) }
  editSubscribe(event) { this._openForm("subscribe", event, this._json(event, "subscribeJson")) }

  removeBubble(event) { this._removeOverlay("bubble", event) }
  removeTitle(event) { this._removeOverlay("title", event) }
  removeLogo(event) { this._removeOverlay("logo", event) }
  removeSubscribe(event) { this._removeOverlay("subscribe", event) }

  _openForm(kind, event, prefill) {
    const existing = this.element.querySelector(`.${kind}-form`)
    if (existing) { existing.remove(); return }

    const isEdit = prefill != null && prefill.index != null
    const form = this._templateFor(kind).content.firstElementChild.cloneNode(true)

    if (isEdit) {
      form.querySelector("[data-form-title]").textContent = `Edit ${this._noun(kind)}`
      form.querySelector("[data-form-submit]").textContent = "Update"
      writeOverlayForm(form, toFormValues(kind, prefill))
      if (kind === "logo") this._prefillLogoSource(form, prefill)
    }

    event.currentTarget.closest(".space-y-1\\.5")?.appendChild(form)

    form.querySelector("[data-role=cancel]").addEventListener("click", () => form.remove())
    if (kind === "logo") this._wireLogoSource(form)
    form.querySelector("[data-role=submit]").addEventListener("click", () =>
      this._submitOverlay(kind, form, prefill, isEdit),
    )

    this._focusFirst(kind, form)
  }

  async _submitOverlay(kind, form, prefill, isEdit) {
    const raw = readOverlayForm(form)

    if (kind === "logo") {
      const srcHidden = form.querySelector("[data-field=src]")
      const srcDisplay = form.querySelector("[data-field=src-display]")
      if (!srcHidden.value) {
        srcDisplay.textContent = "Please select an image first"
        srcDisplay.className = "text-xs text-red-500 flex-1"
        return
      }
      raw.asset_id = srcHidden.dataset.assetId ? parseInt(srcHidden.dataset.assetId, 10) : null
      raw.asset_name = srcDisplay.textContent
    }

    if ((kind === "bubble" || kind === "title") && !raw.text) {
      form.querySelector("[data-field=text]").focus()
      return
    }

    const carried = kind === "bubble"
      ? { start_frame: prefill?.start_frame, draw_duration: prefill?.draw_duration }
      : {}
    const payload = serializeOverlay(kind, raw, carried)
    const field = OVERLAY_KIND_TO_FIELD[kind]

    try {
      if (isEdit) await this.api.updateSegment({ [field]: `__remove__${prefill.index}` })
      const response = await this.api.updateSegment({ [field]: [payload] })
      if (response.ok) window.location.reload()
    } catch {
      // Silent fail — matches prior behaviour.
    }
  }

  async _removeOverlay(kind, event) {
    const index = parseInt(event.currentTarget.dataset[`${kind}Index`], 10)
    const field = OVERLAY_KIND_TO_FIELD[kind]
    try {
      await this.api.updateSegment({ [field]: `__remove__${index}` })
      window.location.reload()
    } catch {
      if (kind === "bubble") alert("Failed to remove speech bubble")
    }
  }

  // --- Copy / paste (cross-clip, cross-tab via the system clipboard) ---

  async copyOverlay(event) {
    event.stopPropagation()
    const button = event.currentTarget
    let payload
    try {
      payload = JSON.parse(button.dataset.overlayJson)
    } catch {
      this._flash(button, "Copy failed", "text-red-500")
      return
    }
    const envelope = buildOverlayEnvelope(button.dataset.overlayKind, payload)
    try {
      await navigator.clipboard.writeText(JSON.stringify(envelope))
      this._flash(button, "Copied", "text-green-600")
    } catch {
      this._flash(button, "Copy denied", "text-red-500")
    }
  }

  async pasteOverlay(event) {
    const button = event.currentTarget
    let text
    try {
      text = await navigator.clipboard.readText()
    } catch {
      this._flash(button, "Clipboard read denied", "text-red-500")
      return
    }

    const envelope = parseOverlayEnvelope(text)
    if (!envelope) { this._flash(button, "No overlay on clipboard", "text-red-500"); return }
    const field = OVERLAY_KIND_TO_FIELD[envelope.kind]
    if (!field) { this._flash(button, "Unknown overlay type", "text-red-500"); return }

    try {
      const response = await this.api.updateSegment({ [field]: [envelope.data] })
      if (response.ok) window.location.reload()
      else this._flash(button, "Paste failed", "text-red-500")
    } catch {
      this._flash(button, "Paste failed", "text-red-500")
    }
  }

  // --- Logo source selection (upload / library browse) ---

  _wireLogoSource(form) {
    const fileInput = form.querySelector("[data-field=logo-file]")
    const srcDisplay = form.querySelector("[data-field=src-display]")
    const srcHidden = form.querySelector("[data-field=src]")
    const libraryGrid = form.querySelector("[data-field=library-grid]")

    form.querySelector("[data-role=choose-file]").addEventListener("click", () => fileInput.click())
    fileInput.addEventListener("change", () =>
      this._uploadLogoFile(fileInput, srcHidden, srcDisplay, libraryGrid),
    )
    form.querySelector("[data-role=browse-library]").addEventListener("click", () =>
      this._browseLibrary(srcHidden, srcDisplay, libraryGrid),
    )
  }

  async _uploadLogoFile(fileInput, srcHidden, srcDisplay, libraryGrid) {
    const file = fileInput.files[0]
    if (!file) return
    srcDisplay.textContent = "Uploading..."
    try {
      const { data } = await this.api.uploadAsset(file, "logo")
      if (data.success) {
        srcHidden.value = data.path
        srcDisplay.textContent = file.name
        srcDisplay.className = "text-xs text-gray-600 truncate flex-1"
        libraryGrid.classList.add("hidden")
      } else {
        srcDisplay.textContent = "Upload failed"
      }
    } catch {
      srcDisplay.textContent = "Upload failed"
    }
  }

  async _browseLibrary(srcHidden, srcDisplay, libraryGrid) {
    if (!libraryGrid.classList.contains("hidden")) { libraryGrid.classList.add("hidden"); return }
    libraryGrid.innerHTML = '<p class="text-xs text-gray-400 py-2">Loading...</p>'
    libraryGrid.classList.remove("hidden")

    const selectAsset = (asset) => {
      srcHidden.value = asset.url
      srcHidden.dataset.assetId = asset.id
      srcDisplay.textContent = asset.name
      srcDisplay.className = "text-xs text-gray-600 truncate flex-1"
      libraryGrid.classList.add("hidden")
      libraryGrid.innerHTML = ""
    }

    try {
      const resp = await this.api.fetchMediaLibrary("image")
      const { assets } = await resp.json()
      if (!assets.length) { libraryGrid.innerHTML = libraryEmptyMarkup(); return }
      if (assets.length === 1) { selectAsset(assets[0]); return }
      populateLibraryGrid(libraryGrid, assets, selectAsset)
    } catch {
      libraryGrid.innerHTML = libraryErrorMarkup()
    }
  }

  _prefillLogoSource(form, p) {
    if (!p.src) return
    const srcHidden = form.querySelector("[data-field=src]")
    const srcDisplay = form.querySelector("[data-field=src-display]")
    srcHidden.value = p.src
    if (p.asset_id != null) srcHidden.dataset.assetId = p.asset_id
    srcDisplay.textContent = p.src.split("/").pop()
    srcDisplay.className = "text-xs text-gray-600 truncate flex-1"
  }

  // --- Small helpers ---

  _templateFor(kind) {
    return {
      bubble: this.bubbleTemplateTarget,
      title: this.titleTemplateTarget,
      logo: this.logoTemplateTarget,
      subscribe: this.subscribeTemplateTarget,
    }[kind]
  }

  _noun(kind) {
    return {
      bubble: "speech bubble",
      title: "title overlay",
      logo: "logo overlay",
      subscribe: "subscribe button",
    }[kind]
  }

  _focusFirst(kind, form) {
    if (kind === "bubble" || kind === "title") form.querySelector("[data-field=text]")?.focus()
    else if (kind === "logo") form.querySelector("[data-field=src]")?.focus()
  }

  _json(event, key) {
    return JSON.parse(event.currentTarget.dataset[key])
  }

  _flash(button, message, colorClass) {
    const original = button.innerHTML
    button.innerHTML = `<span class="text-[10px] ${colorClass}">${message}</span>`
    setTimeout(() => { button.innerHTML = original }, 1500)
  }
}
